import React, { useState, useEffect, useRef } from "react";
import mockCss from "../Mock.module.css"
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { setFetching, setExamDetails } from "../../../redux/states";
import { checkAuthenticatedUser, useCountdown, formatLargeText } from "../../../utils/client-functions";
import { useErrorSetter } from '../../../hooks/useUiMessages';
import { ButtonThin, ButtonSubmitGreen } from "../../UI/Buttons/Buttons";
import { TfiWrite } from "react-icons/tfi";
import { PiClockCountdownFill } from "react-icons/pi";
import { FaPowerOff, FaChevronLeft, FaChevronRight, FaCheckCircle, FaFlag } from "react-icons/fa";
import styled, { keyframes, css } from 'styled-components';
import { FaLongArrowAltLeft } from "react-icons/fa";
import { PlainModalOverlay, Modal } from "../../UI/Modal/Modal";
import AuthInput from "../../UI/Input/AuthInputs";
import { useConfirm } from 'material-ui-confirm';
import { useTheme } from "../../UI/Theme/ThemeContext";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- STYLED COMPONENTS (FUTURISTIC REDESIGN) ---

const PageWrapper = styled.div`
    min-height: 100vh;
    background: ${({ theme }) => theme.background === '#fff' ? '#f4f7f8' : theme.background};
    color: ${({ theme }) => theme.text};
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    transition: all 0.3s ease;
    padding-bottom: 5rem;
    position: relative;
    overflow-x: hidden;
`;

const GlassHeader = styled.header`
    position: sticky;
    top: 0;
    z-index: 100;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    background: ${({ theme }) => theme.background === '#fff' 
        ? 'rgba(255, 255, 255, 0.8)' 
        : 'rgba(12, 30, 40, 0.85)'};
    border-bottom: 1px solid ${({ theme }) => theme.background === '#fff' 
        ? 'rgba(0,0,0,0.05)' 
        : 'rgba(255,255,255,0.05)'};
    padding: 0.75rem 1.5rem;
    padding-top: 3rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 4px 20px rgba(0,0,0,0.03);

    @media (max-width: 600px) {
        padding: 0.5rem 1rem;
    }
`;

const HeaderSection = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const TimerBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 6px 14px;
    background: ${({ $isLow, theme }) => $isLow 
        ? 'rgba(214, 7, 7, 0.1)' 
        : (theme.background === '#fff' ? 'rgba(111, 203, 209, 0.1)' : 'rgba(111, 203, 209, 0.05)')};
    border-radius: 99px;
    border: 1px solid ${({ $isLow }) => $isLow ? '#d80707' : '#6FCBD1'};
    color: ${({ $isLow }) => $isLow ? '#d80707' : '#6FCBD1'};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 0.95rem;
    transition: all 0.3s ease;
`;

const SubjectTitle = styled.h1`
    font-size: 1rem;
    font-weight: 700;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6FCBD1;

    @media (max-width: 500px) {
        display: none;
    }
`;

const MainContent = styled.main`
    max-width: 1200px;
    margin: 2rem auto;
    padding: 0 1.5rem;
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 2rem;
    align-items: start;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        margin: 1rem auto;
    }
`;

const QuestionContainer = styled.div`
    animation: ${fadeIn} 0.4s ease-out;
`;

const ModernCard = styled.div`
    background: ${({ theme }) => theme.background === '#fff' ? '#ffffff' : 'rgba(255,255,255,0.02)'};
    border: 1px solid ${({ theme }) => theme.background === '#fff' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 10px 40px rgba(0,0,0,0.05);
    position: relative;
    overflow: hidden;

    @media (max-width: 600px) {
        padding: 1.5rem;
    }
`;

const QuestionInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
`;

const Badge = styled.span`
    padding: 4px 12px;
    background: ${({ $variant }) => $variant === 'secondary' ? 'rgba(241, 196, 15, 0.1)' : 'rgba(111, 203, 209, 0.1)'};
    color: ${({ $variant }) => $variant === 'secondary' ? '#f1c40f' : '#6FCBD1'};
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const QuestionText = styled.div`
    font-size: 1.2rem;
    line-height: 1.6;
    font-weight: 500;
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.text};

    @media (max-width: 600px) {
        font-size: 1.1rem;
    }
`;

const OptionsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const OptionContainer = styled.label`
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    border: 2px solid ${({ $isSelected, theme }) => $isSelected 
        ? '#6FCBD1' 
        : (theme.background === '#fff' ? '#f0f0f0' : 'rgba(255,255,255,0.05)')};
    background: ${({ $isSelected, theme }) => $isSelected 
        ? 'rgba(111, 203, 209, 0.05)' 
        : (theme.background === '#fff' ? '#fff' : 'rgba(255,255,255,0.01)')};
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:hover {
        border-color: #6FCBD1;
        transform: translateX(4px);
        background: rgba(111, 203, 209, 0.02);
    }

    input {
        position: absolute;
        opacity: 0;
    }

    .radio-circle {
        width: 20px;
        height: 20px;
        border: 2px solid ${({ $isSelected }) => $isSelected ? '#6FCBD1' : '#ccc'};
        border-radius: 50%;
        margin-right: 1.2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background: ${({ $isSelected }) => $isSelected ? '#6FCBD1' : 'transparent'};
        transition: all 0.2s ease;

        &::after {
            content: '';
            width: 8px;
            height: 8px;
            background: white;
            border-radius: 50%;
            display: ${({ $isSelected }) => $isSelected ? 'block' : 'none'};
        }
    }

    .option-content {
        font-size: 1rem;
        font-weight: 500;
    }
`;

const Sidebar = styled.aside`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    position: sticky;
    top: 5rem;

    @media (max-width: 992px) {
        position: static;
    }
`;

const NavigatorGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
    gap: 0.6rem;
    background: ${({ theme }) => theme.background === '#fff' ? '#fff' : 'rgba(255,255,255,0.02)'};
    padding: 1.2rem;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.background === '#fff' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
`;

const NavItem = styled.button`
    aspect-ratio: 1;
    border-radius: 10px;
    border: 1px solid ${({ $isActive, $isAnswered, $isFlagged }) => 
        $isActive ? '#6FCBD1' : ($isFlagged ? '#f1c40f' : ($isAnswered ? 'rgba(111, 203, 209, 0.4)' : 'rgba(0,0,0,0.05)'))
    };
    background: ${({ $isActive, $isAnswered, $isFlagged, theme }) => 
        $isActive ? '#6FCBD1' : 
        ($isFlagged ? 'rgba(241, 196, 15, 0.15)' : ($isAnswered ? 'rgba(111, 203, 209, 0.15)' : (theme.background === '#fff' ? '#f8f9fa' : 'rgba(255,255,255,0.03)')))
    };
    color: ${({ $isActive, theme }) => $isActive ? '#fff' : theme.text};
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &:hover {
        transform: scale(1.1);
        border-color: #6FCBD1;
    }

    ${({ $isAnswered, $isActive, $isFlagged }) => ($isAnswered || $isFlagged) && !$isActive && css`
        &::after {
            content: '';
            position: absolute;
            top: 4px;
            right: 4px;
            width: 5px;
            height: 5px;
            background: ${({ $isFlagged }) => $isFlagged ? '#f1c40f' : '#6FCBD1'};
            border-radius: 50%;
        }
    `}
`;

const ControlBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.background === '#fff' 
        ? 'rgba(255, 255, 255, 0.9)' 
        : 'rgba(12, 30, 40, 0.95)'};
    backdrop-filter: blur(10px);
    border-top: 1px solid ${({ theme }) => theme.background === '#fff' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: center;
    z-index: 90;
`;

const ControlBarInner = styled.div`
    max-width: 1200px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 10px 24px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border: none;

    ${({ $variant }) => $variant === 'primary' ? css`
        background: linear-gradient(135deg, #6FCBD1, #3E8F93);
        color: white;
        box-shadow: 0 4px 15px rgba(62, 143, 147, 0.3);
        &:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(62, 143, 147, 0.4); }
    ` : css`
        background: ${({ theme }) => theme.background === '#fff' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
        color: ${({ theme }) => theme.text};
        &:hover { background: rgba(111, 203, 209, 0.1); color: #6FCBD1; }
    `}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none !important;
    }

    @media (max-width: 500px) {
        padding: 10px 16px;
        span { display: none; }
    }
`;

const StyledAbortButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: transparent;
    border: 1px solid rgba(255, 77, 77, 0.3);
    color: #ff4d4d;
    padding: 6px 14px;
    border-radius: 99px;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 77, 77, 0.1);
        border-color: #ff4d4d;
    }

    @media (max-width: 500px) {
        padding: 6px 10px;
        span { display: none; }
    }
`;

const DiagramWrapper = styled.div`
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 2rem;
    border: 1px solid rgba(111, 203, 209, 0.2);
    background: white;
    max-width: 100%;
    
    img {
        width: 100%;
        display: block;
        max-height: 400px;
        object-fit: contain;
    }
`;

const ProgressTrack = styled.div`
    height: 4px;
    background: ${({ theme }) => theme.background === '#fff' ? '#eee' : 'rgba(255,255,255,0.1)'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
`;

const ProgressFill = styled.div`
    height: 100%;
    background: #6FCBD1;
    width: ${({ percentage }) => percentage}%;
    transition: width 0.4s ease-out;
    box-shadow: 0 0 10px rgba(111, 203, 209, 0.5);
`;

const Subtitle = styled.p`
    font-size: 0.9rem;
    opacity: 0.7;
    margin-bottom: 2.5rem;
    line-height: 1.6;
    background: ${({ theme }) => theme.background === '#fff' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'};
    padding: 1rem;
    border-radius: 12px;
    border-left: 4px solid #6FCBD1;
`;

// --- MAIN COMPONENT ---

const ExamStarted = (props) => {
    const { examDetails } = useSelector((state) => state.stateData);
    const { themeName } = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const confirm = useConfirm();

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [timeAlloted, setTimeAlloted] = useState(20); 
    const [subject, setSubject] = useState("");
    const [answers, setAnswers] = useState({});
    const [flagged, setFlagged] = useState({});
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [isTimeOut, setIsTimeOut] = useState(false);
    const [readyToSubmit, setReadyToSubmit] = useState(false);
    
    const [questionStartTime, setQuestionStartTime] = useState(Date.now());
    const [progressStatus, setProgressStatus] = useState('Submitting...');
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showFailedSubmissionModal, setShowFailedSubmissionModal] = useState(false);

    const [showReturnButton, setShowReturnButton] = useState(false);
    const submitTimerRef = useRef(null);
    const returnBtnTimerRef = useRef(null);

    const countdown = useCountdown(timeAlloted);
    const isAuth = localStorage?.getItem("token");
    const errorSetter = useErrorSetter();

    const answeredCount = Object.keys(answers).length;
    const progressPerc = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

    const handleAbort = () => {
        confirm({ 
            title: 'Abort Exam?',
            description: 'Are you sure you want to quit? Your progress will not be saved.',
            confirmationText: 'Yes, Abort',
            cancellationText: 'Stay',
            confirmationButtonProps: { color: 'error' }
        })
        .then(() => {
            dispatch(setExamDetails({}));
            navigate('/user/dashboard/mock');
        })
        .catch(() => {});
    };
    
    const checkIfAuth = async () => {
        try {
            await checkAuthenticatedUser();
        } catch (error) {
            dispatch(setFetching(false));
            return navigate("/popin?mock");
        }
    };
    
    const handleSubmit = async () => {
        await checkIfAuth();

        const fiftyPercentOfQ = Math.floor(totalQuestions * 0.5); 
        if (Object.keys(answers).length < fiftyPercentOfQ) {
            errorSetter(`Please attempt at least 50% (${fiftyPercentOfQ}) of the questions before submitting.`);
            return;
        }

        setLoading(true);
        setReadyToSubmit(false);
        setProgressStatus("Beginning exam submission...");
        setProgressPercentage(5);

        try {
            const examObj = {
                examMeta: examDetails.examMeta,
                questions: questions
            };
            
            const response = await axios.post("/mock/submit-exam-job", examObj, {
                headers: { "x-access-token": isAuth },
            });
            
            if (!response.data.jobId) {
                setLoading(false);
                errorSetter(response.data.error || "Failed to submit exam. Please try again.");
                return;
            }

            const jobId = response.data.jobId;
            const evtSource = new EventSource(`/mock/exam-grading-progress/${jobId}`);

            evtSource.onerror = (error) => {
                setLoading(false);
                evtSource.close();
                setShowFailedSubmissionModal(true);
            };

            evtSource.addEventListener("progress", (e) => {
                try {
                    const { status, progress } = JSON.parse(e.data);
                    setProgressStatus(status || "Grading exam...");
                    setProgressPercentage(progress || 10);
                } catch { setProgressStatus("Grading in progress..."); }
            });

            evtSource.addEventListener("done", (e) => {
                try {
                    const result = JSON.parse(e.data);
                    const gradedExamId = result.gradedExamId;
                    dispatch(setExamDetails({}));
                    setLoading(false);
                    evtSource.close();
                    navigate(`/user/dashboard/mock/exam-insight?examId=${gradedExamId}`);
                } catch (error) {
                    setLoading(false);
                    evtSource.close();
                    setShowFailedSubmissionModal(true);
                }
            });

            evtSource.addEventListener("error", (e) => {
                setLoading(false);
                evtSource.close();
                setShowFailedSubmissionModal(true);
            });
        } catch (error) {
            setLoading(false);
            if (error?.response?.data?.openPricing) {
                window.dispatchEvent(new CustomEvent('openExamPricing'));
            }
            const msg = error?.response?.data?.message || error?.response?.data?.error || "Failed to submit exam. Please try again.";
            if (error?.response?.status === 403 && error?.response?.data?.status === 'quota') {
                errorSetter(msg);
            } else {
                setShowFailedSubmissionModal(true);
            }
        }
    };
   
    useEffect(() => {
        setQuestionStartTime(Date.now());
    }, [currentQuestion]);

    useEffect(() => {
        let isMounted = true;
        const initializeExam = async () => {
            dispatch(setFetching(true));
            try {
                await checkIfAuth();
                if (!examDetails || Object.keys(examDetails).length === 0) {
                    errorSetter("No exam found. Redirecting to mock dashboard...");
                    setTimeout(() => { navigate("/user/dashboard/mock"); }, 5000);
                    return;
                }
                if (isMounted) {
                    setQuestions(examDetails.questions);
                    setTotalQuestions(examDetails.questions.length);
                    setTimeAlloted(examDetails.examMeta.duration);
                    setSubject(examDetails.examMeta.subject);
                }
            } catch (err) {
                errorSetter("An error occurred while initializing the exam.");
                setTimeout(() => { navigate("/user/dashboard/mock"); }, 5000);
            } finally {
                if (isMounted) dispatch(setFetching(false));
            }
        };

        initializeExam();
        return () => { isMounted = false; };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (countdown === "00:00" || countdown === "00:00:00") {
            setIsTimeOut(true);
            if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
            if (returnBtnTimerRef.current) clearTimeout(returnBtnTimerRef.current);
            setShowReturnButton(false);

            returnBtnTimerRef.current = setTimeout(() => { setShowReturnButton(true); }, 5000);
            submitTimerRef.current = setTimeout(() => { handleSubmit(); }, 5000);
        }

        return () => {
            if (submitTimerRef.current) { clearTimeout(submitTimerRef.current); submitTimerRef.current = null; }
            if (returnBtnTimerRef.current) { clearTimeout(returnBtnTimerRef.current); returnBtnTimerRef.current = null; }
        };
        // eslint-disable-next-line
    }, [countdown]);

    const handleOptionChange = (qNum, value, isText = false) => {
        const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
        setAnswers(prev => ({ ...prev, [qNum]: value }));
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.question_number === qNum
                    ? {
                        ...q,
                        chosen_answer: isText ? value : q.options[value],
                        time_taken: timeSpent,
                        attempts: (q.attempts || 0) + 1
                    }
                    : q
            )
        );
        setQuestionStartTime(Date.now());
    };

    const handleTextAnswerChange = (qNum, e) => {
        handleOptionChange(qNum, e.target.value, true);
    };

    const goToQuestion = (idx) => {
        setCurrentQuestion(idx);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const handlePrev = () => {
        if (currentQuestion > 0) goToQuestion(currentQuestion - 1);
    }
    const toggleFlag = (qNum) => {
        setFlagged(prev => ({ ...prev, [qNum]: !prev[qNum] }));
    };

    const handleNext = () => {
        if (currentQuestion < totalQuestions - 1) goToQuestion(currentQuestion + 1);
        window.scrollTo(0,0)
    }

    const currentQ = questions[currentQuestion];

    return (
        <PageWrapper>
            <ProgressTrack>
                <ProgressFill percentage={progressPerc} />
            </ProgressTrack>

            <GlassHeader>
                <HeaderSection>
                    <StyledAbortButton onClick={handleAbort}>
                        <FaPowerOff /> <span>Abort Exam</span>
                    </StyledAbortButton>
                    <SubjectTitle title={subject}>
                        <TfiWrite /> {subject || "Mock Exam"}
                    </SubjectTitle>
                </HeaderSection>
                
                <HeaderSection>
                    <TimerBadge $isLow={countdown && countdown.startsWith('00:') && parseInt(countdown.split(':')[1]) < 5}>
                        <PiClockCountdownFill /> {countdown}
                    </TimerBadge>
                </HeaderSection>
            </GlassHeader>

            <MainContent>
                <QuestionContainer key={currentQuestion}>
                    {currentQ && (
                        <ModernCard>
                            <QuestionInfo>
                                <Badge>Question {currentQ.question_number} of {totalQuestions}</Badge>
                                <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                                    <button 
                                        onClick={() => toggleFlag(currentQ.question_number)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: flagged[currentQ.question_number] ? '#f1c40f' : '#ccc',
                                            cursor: 'pointer',
                                            fontSize: '1.2rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            transition: 'transform 0.2s'
                                        }}
                                        title="Flag for review"
                                    >
                                        <FaFlag style={{ transform: flagged[currentQ.question_number] ? 'scale(1.2)' : 'scale(1)' }} />
                                    </button>
                                    {currentQ.section && <Badge $variant="secondary">{currentQ.section}</Badge>}
                                </div>
                            </QuestionInfo>

                            {currentQ.instructions && (
                                <Subtitle>
                                    {formatLargeText(currentQ.instructions)}
                                </Subtitle>
                            )}

                            {currentQ.diagram && currentQ.diagram.trim() !== "" && (
                                <DiagramWrapper>
                                    <img src={currentQ.diagram} alt="Question Diagram" />
                                </DiagramWrapper>
                            )}

                            <QuestionText>
                                {formatLargeText(currentQ.question_text)}
                            </QuestionText>

                            <OptionsGrid>
                                {currentQ.options?.length > 0 || currentQ?.isObj === true ? (
                                    currentQ.options.map((opt, idx) => (
                                        <OptionContainer 
                                            key={idx} 
                                            $isSelected={answers[currentQ.question_number] === idx}
                                        >
                                            <input
                                                type="radio"
                                                name={`question_${currentQuestion}`}
                                                checked={answers[currentQ.question_number] === idx}
                                                onChange={() => handleOptionChange(currentQ.question_number, idx)}
                                            />
                                            <div className="radio-circle" />
                                            <div className="option-content">{opt}</div>
                                        </OptionContainer>
                                    ))
                                ) : (
                                    <AuthInput
                                        id={`question_${currentQ.question_number}`}
                                        name={`question_${currentQ.question_number}`}
                                        value={answers[currentQ.question_number] || ""}
                                        placeholder="Type your answer here..."
                                        multiline={true}
                                        rows={6}
                                        onChange={e => handleTextAnswerChange(currentQ.question_number, e)}
                                    />
                                )}
                            </OptionsGrid>
                        </ModernCard>
                    )}
                </QuestionContainer>

                <Sidebar>
                    <div style={{fontWeight: 700, fontSize: '0.9rem', marginBottom: '-0.5rem', display: 'flex', justifyContent: 'space-between'}}>
                        <span>Quick Navigator</span>
                        <span style={{color: '#6FCBD1'}}>{answeredCount} / {totalQuestions}</span>
                    </div>
                    <NavigatorGrid $shadow={themeName === 'dark'}>
                        {questions.map((q, idx) => (
                            <NavItem
                                key={q.question_number}
                                $isActive={currentQuestion === idx}
                                $isAnswered={answers[q.question_number] !== undefined}
                                $isFlagged={flagged[q.question_number]}
                                onClick={() => goToQuestion(idx)}
                                title={`Go to question ${q.question_number}`}
                            >
                                {q.question_number}
                            </NavItem>
                        ))}
                    </NavigatorGrid>
                    
                    <ButtonSubmitGreen onClick={() => setReadyToSubmit(true)} style={{padding: '16px', borderRadius: '16px'}}>
                        <FaCheckCircle /> <b>Finish & Submit</b>
                    </ButtonSubmitGreen>
                </Sidebar>
            </MainContent>

            <ControlBar>
                <ControlBarInner>
                    <ActionButton 
                        onClick={handlePrev} 
                        disabled={currentQuestion === 0}
                    >
                        <FaChevronLeft /> <span>Previous</span>
                    </ActionButton>

                    <div style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.6 }}>
                        {currentQuestion + 1} / {totalQuestions}
                    </div>

                    {currentQuestion === totalQuestions - 1 ? (
                        <ActionButton 
                            $variant="primary" 
                            onClick={() => setReadyToSubmit(true)}
                        >
                            <span>Finish Exam</span> <FaFlag />
                        </ActionButton>
                    ) : (
                        <ActionButton 
                            $variant="primary" 
                            onClick={handleNext}
                        >
                            <span>Next</span> <FaChevronRight />
                        </ActionButton>
                    )}
                </ControlBarInner>
            </ControlBar>

            {/* MODALS (STYLING KEPT COMPATIBLE BUT CLEANED UP) */}

            {readyToSubmit && (
                <PlainModalOverlay>
                    <div className='prev-page' onClick={() => setReadyToSubmit(false)}>
                        <FaLongArrowAltLeft />
                    </div>
                    <div className={mockCss.modalOverlayContainer}>
                        <h2 className={mockCss.modalOverlayHeader} style={{color: '#6FCBD1'}}>
                            Submit Exam?
                        </h2>

                        <div className={mockCss.modalOverlayBody} style={{textAlign: 'center'}}>
                            {(() => {
                                const unanswered = questions.filter(
                                    q => answers[q.question_number] === undefined
                                );
                                if (unanswered.length > 0) {
                                    return (
                                        <>
                                            <div style={{ color: "#ff4d4d", marginBottom: 15, fontWeight: 700 }}>
                                                You have {unanswered.length} unanswered questions.
                                            </div>
                                            <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                                                {unanswered.map(q => (
                                                    <span
                                                        key={q.question_number}
                                                        onClick={() => { goToQuestion(questions.indexOf(q)); setReadyToSubmit(false); }}
                                                        style={{
                                                            padding: "4px 10px",
                                                            background: themeName === 'dark' ? "rgba(255, 77, 77, 0.1)" : "#fff5f5",
                                                            color: "#ff4d4d",
                                                            borderRadius: "8px",
                                                            fontWeight: 700,
                                                            fontSize: "0.8rem",
                                                            border: "1px solid rgba(255, 77, 77, 0.2)",
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Q{q.question_number}
                                                    </span>
                                                ))}
                                            </div>
                                            <p>Are you sure you want to finish now?</p>
                                        </>
                                    );
                                } else {
                                    return <p style={{fontSize: '1.1rem'}}>Excellent! All questions answered. Ready to see your results?</p>;
                                }
                            })()}
                        </div>

                        <div className={mockCss.modalOverlayStartBtn}>
                            <ButtonSubmitGreen type="button" onClick={handleSubmit}>
                                <b>Yes, Submit My Exam</b>
                            </ButtonSubmitGreen>
                        </div>
                    </div>
                </PlainModalOverlay>
            )}

            {isTimeOut && (
                <PlainModalOverlay>
                    <div className={mockCss.modalOverlayContainer}>
                        <h2 className={mockCss.modalOverlayHeader} style={{ color: "#ff4d4d" }}>
                            Time is Up!
                        </h2>
                        <div className={mockCss.modalOverlayBody} style={{textAlign: 'center'}}>
                            <div style={{ marginBottom: 16, fontSize: '1.1rem' }}>
                                Your answers are being collected for grading.
                            </div>
                            <h6 style={{ color: "#ff4d4d", fontSize: '1rem' }}>
                                Auto-submitting in 5 seconds if more than 50% of questions are answered...
                            </h6>
                            {showReturnButton && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                    <ButtonThin onClick={() => {
                                        if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
                                        setShowReturnButton(false);
                                        dispatch(setExamDetails({}));
                                        navigate('/user/dashboard/mock');
                                    }}>
                                        <FaLongArrowAltLeft /> &nbsp; Back to Tests
                                    </ButtonThin>
                                </div>
                            )}
                        </div>
                    </div>
                </PlainModalOverlay>
            )}

            {showFailedSubmissionModal && (
                <PlainModalOverlay>
                    <div className={mockCss.modalOverlayContainer}>
                        <h2 className={mockCss.modalOverlayHeader} style={{ color: "#ff4d4d" }}>
                            Submission Failed
                        </h2>
                        <div className={mockCss.modalOverlayBody} style={{textAlign: 'center'}}>
                            <p>We encountered an error while grading your exam. Please try again.</p>
                            <div style={{width: "80%", margin: "20px auto"}}>
                                <ButtonSubmitGreen onClick={handleSubmit}>
                                    <b>Retry Submission</b>
                                </ButtonSubmitGreen>
                            </div>
                        </div>
                    </div>
                </PlainModalOverlay>
            )}
            
            {loading && (
                <Modal
                    header4={`ANALYZING ANSWERS... DO NOT RELOAD`}
                    header3={progressStatus}
                    progress={progressPercentage}
                />
            )}
        </PageWrapper>
    );
};

export default ExamStarted;
